/**
 * Platform engine initialization for registry-demo.
 * Initializes the harmonizer registry and wires all tool implementations.
 */

import {
  initializeRegistry,
  wireToolImplementation,
  registerToolSpecs,
  getAllAdapters,
  type CanonicalToolCategory,
} from "../harmonizer";
import * as toolRunners from "../tools";
import type { NormalizedToolSpec } from "../harmonizer/specLoader";

/**
 * Creates a minimal tool spec for browser registration.
 * Used when TOOL_SPEC files aren't available.
 */
function createMinimalToolSpec(
  toolName: string,
  category: CanonicalToolCategory
): NormalizedToolSpec {
  // Create a minimal raw spec structure
  const rawSpec = {
    tool_spec_version: "1.0",
    identity: {
      tool_name: toolName,
      short_label: toolName,
      description: `${toolName} prioritization tool`,
      category: category,
      status: "active",
    },
    semantics: {
      purpose: "Prioritize items",
      requires_comparable_items: true,
    },
    inputs: {
      required_item_fields: ["id", "label"],
      required_item_attributes: [],
      optional_item_attributes: [],
      criteria_set_contract: {
        allowed_criteria_ids: "dynamic",
        requires_explicit_criteria_set: false,
      },
      lens_support: {
        requires_lens: false,
      },
      tool_config: {
        fields: [],
        supports_defaults: false,
      },
    },
    algorithm: {
      score_model_type: "numeric",
    },
    outputs: {
      score_set_schema: {
        score_fields: ["numericScore", "rank"],
      },
    },
    interaction_model: {
      ui_pattern: "form",
      step_flow: ["configure", "run"],
      supports_inline_edit: false,
      supports_batch_edit: false,
    },
    constraints_and_assumptions: {
      max_items: 1000,
      performance_notes: "",
    },
    test_vectors: [],
  };

  return {
    raw: rawSpec as any,
    toolName,
    category,
    flags: {
      isArrangeCapable: true,
    },
    inputContract: {
      requiredItemFields: ["id", "label"],
      requiredAttributes: [],
      optionalAttributes: [],
      criteriaSetContract: {
        allowedCriteriaIds: [],
        requiresExplicitCriteriaSet: false,
      },
      lensRequired: false,
      toolConfigFields: [],
    },
    interactionModel: {
      uiPattern: "form",
      stepFlow: ["configure", "run"],
      supportsInlineEdit: false,
      supportsBatchEdit: false,
    },
  };
}

/**
 * Initializes the platform engine.
 * Must be called once at app startup before any tool operations.
 */
export function initPlatformEngine(): void {
  // Initialize the harmonizer registry (will be empty in browser)
  initializeRegistry();
  
  // Check if registry is empty (browser mode)
  let adapters: any[] = [];
  try {
    adapters = getAllAdapters();
  } catch (e) {
    // Registry not initialized or empty
  }

  // If registry is empty, manually register minimal tool specs
  if (adapters.length === 0) {
    console.log("Registry empty - registering tools manually for browser mode");
    
    const minimalSpecs: NormalizedToolSpec[] = [
      createMinimalToolSpec("RICE", "scoring_model"),
      createMinimalToolSpec("WSJF", "scoring_model"),
      createMinimalToolSpec("DotVoting", "voting_model"),
      createMinimalToolSpec("Pairwise Ranking", "comparison_model"),
      createMinimalToolSpec("MOSCOW", "categorization_model"),
      createMinimalToolSpec("IMPACT_EFFORT_MATRIX", "categorization_model"),
      createMinimalToolSpec("Simple Weighted Scoring", "scoring_model"),
      createMinimalToolSpec("PARETO", "categorization_model"),
      createMinimalToolSpec("CostOfDelay", "scoring_model"),
      createMinimalToolSpec("Buy A Feature", "voting_model"),
      createMinimalToolSpec("Max Priorities Pyramid", "categorization_model"),
      createMinimalToolSpec("Stack Ranking", "comparison_model"),
    ];
    
    registerToolSpecs(minimalSpecs);
    console.log("✓ Registered minimal tool specs for browser mode");
  }

  // Wire all tool implementations
  const toolRunnerMap: Record<string, any> = {
    "RICE": toolRunners.runRiceTool,
    "WSJF": toolRunners.runWsjfTool,
    "DotVoting": toolRunners.runDotVotingTool,
    "Pairwise Ranking": toolRunners.runPairwiseRankingTool,
    "MOSCOW": toolRunners.runMoscowTool,
    "IMPACT_EFFORT_MATRIX": toolRunners.runImpactEffortTool,
    "Simple Weighted Scoring": toolRunners.runSimpleWeightedScoringTool,
    "PARETO": toolRunners.runParetoAnalysisTool,
    "CostOfDelay": toolRunners.runCostOfDelayTool,
    "Buy A Feature": toolRunners.runBuyAFeatureTool,
    "Max Priorities Pyramid": toolRunners.runMaxPrioritiesPyramidTool,
    "Stack Ranking": toolRunners.runStackRankingTool,
  };

  Object.entries(toolRunnerMap).forEach(([toolName, runner]) => {
    if (runner) {
      try {
        wireToolImplementation(toolName, runner);
        console.log(`✓ Wired tool: ${toolName}`);
      } catch (error) {
        console.warn(`⚠ Could not wire tool ${toolName}:`, error);
      }
    }
  });
}

